import {
  FUSION_COUNT,
  type FuseRejected,
  type FuseResult,
  getItem,
  type InventoryState,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { benchHead, formatGold, purseChip, setPurse } from './bench.js';
import { RARITY_WORDS, renderFusionDetail } from './fusion-detail.js';
import { type FusionGroup, fusionGroups } from './fusion-groups.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { paintItemIcon } from './item-icon.js';
import { kitTitle } from './kit.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './fusion.css';

/**
 * The smelter's window (ADR 0018): the bag's pieces grouped by kind, how
 * many of the five each group has, the piece five would make, its price,
 * and the button. Like every panel it only sends the intent; what the bag
 * holds afterwards is the server's.
 */

export interface FusionPanelOptions {
  icons: CssAtlas | null;
  onFuse: (templateId: string) => void;
  /** Wear the piece just made, from the bag (ADR 0078): the next thing one wants of it. */
  onEquip: (itemId: string) => void;
}

const REFUSALS: Record<FuseRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'not-fusable': 'Cette pièce ne se fusionne pas.',
  'missing-pieces': 'Il faut {count} pièces identiques dans ton sac.',
  'missing-gold': 'Ta bourse n’y suffit pas.',
  'too-far': 'Approche-toi du fondeur.',
};

export class FusionPanel {
  private readonly root: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly list: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly message: HTMLElement;
  private inventory: InventoryState | undefined;
  private selected: string | undefined;
  /** The piece the last fusion made, offered to wear while it waits in the bag. */
  private made: string | undefined;
  private inReach = false;
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: FusionPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith fusion';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Fondeur'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'smelter');
    title.textContent = uiText('Fondeur');
    const close = closeButton('Fermer (Échap)', () => {
      this.close();
    });
    title.append(close);

    this.purse = purseChip();
    const head = benchHead(
      uiFormat(
        '{count} pièces identiques de ton sac en font une de la rareté au-dessus. Les pièces portées ne comptent pas ; leurs améliorations sont perdues.',
        { count: FUSION_COUNT },
      ),
      this.purse,
    );
    this.list = document.createElement('div');
    this.list.className = 'smith__list';
    this.detail = document.createElement('div');
    this.detail.className = 'smith__detail';
    const body = document.createElement('div');
    body.className = 'smith__body';
    body.append(this.list, this.detail);
    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, head, body, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.render();
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.pending = false;
    this.render();
  }

  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showResult(result: FuseResult): void {
    this.pending = false;
    const made = getItem(result.to);
    this.message.textContent = uiFormat('Fusion réussie : {name} ({rarity}) pour {gold}.', {
      name: uiText(made?.name ?? result.to),
      rarity: made?.rarity === undefined ? '' : uiText(RARITY_WORDS[made.rarity]),
      gold: formatGold(result.gold),
    });
    this.message.dataset.tone = 'success';
    this.root.classList.remove('smith--success');
    this.root.getBoundingClientRect();
    this.root.classList.add('smith--success');
    this.selected = result.to;
    this.made = result.to;
    this.render();
  }

  showRejected(rejection: FuseRejected): void {
    this.pending = false;
    this.message.textContent = uiFormat(REFUSALS[rejection.reason], { count: FUSION_COUNT });
    this.message.dataset.tone = 'refused';
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private render(): void {
    if (this.root.hidden) return;
    setPurse(this.purse, this.inventory?.gold ?? 0);
    const groups = fusionGroups(this.inventory);
    if (!groups.some((group) => group.template.id === this.selected)) {
      this.selected = groups[0]?.template.id;
    }
    this.list.replaceChildren();
    for (const group of groups) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'slot smith__piece';
      cell.dataset.template = group.template.id;
      if (group.template.id === this.selected) cell.classList.add('smith__piece--selected');
      if (group.worn > 0) cell.classList.add('fusion__piece--worn');
      cell.title =
        group.worn > 0
          ? uiFormat('{name} · {worn} portée(s), hors fusion', {
              name: uiText(group.template.name),
              worn: group.worn,
            })
          : uiText(group.template.name);
      const item = document.createElement('div');
      item.className = `item item--${group.template.rarity}`;
      paintItemIcon(item, group.template, this.options.icons, group.template.id);
      const count = document.createElement('span');
      count.className =
        group.count >= FUSION_COUNT ? 'fusion__count fusion__count--ready' : 'fusion__count';
      count.textContent = `${String(Math.min(group.count, 99))}/${String(FUSION_COUNT)}`;
      item.append(count);
      cell.append(item);
      cell.addEventListener('click', () => {
        this.selected = group.template.id;
        this.message.textContent = '';
        this.render();
      });
      this.list.append(cell);
    }
    if (groups.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'smith__muted';
      empty.textContent = uiText('Aucune pièce à fusionner dans ton sac.');
      this.list.append(empty);
    }
    this.renderDetail(groups.find((group) => group.template.id === this.selected));
    this.renderWear();
  }

  /** The piece just made, while it is in the bag and can be worn: one click puts it on. */
  private renderWear(): void {
    const made = this.made === undefined ? undefined : getItem(this.made);
    const waiting = this.inventory?.bag.find((entry) => entry.item.templateId === this.made);
    if (made?.kind !== 'equipment' || waiting === undefined) {
      this.made = undefined;
      return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smith__go fusion__wear';
    button.textContent = uiFormat('Équiper : {name}', { name: uiText(made.name) });
    button.addEventListener('click', () => {
      this.options.onEquip(waiting.item.id);
    });
    this.detail.append(button);
  }

  private renderDetail(group: FusionGroup | undefined): void {
    hideItemHover();
    this.detail.replaceChildren();
    if (group === undefined) return;
    renderFusionDetail(this.detail, {
      template: group.template,
      count: group.count,
      worn: group.worn,
      icons: this.options.icons,
      purse: this.inventory?.gold ?? 0,
      inReach: this.inReach,
      pending: this.pending,
      onFuse: (templateId) => {
        this.pending = true;
        this.options.onFuse(templateId);
      },
    });
  }
}
